/**
 * useProcessVariance Hook
 *
 * Provides access to EWMA-based process variance predictions, statistics,
 * and seasonal patterns from the process-variance Edge Function.
 *
 * Part of US-P3-001: Statistical Process Variance Learning
 *
 * @example
 * const {
 *   predictions,
 *   statistics,
 *   seasonalPatterns,
 *   getPrediction,
 *   getConfidence,
 *   isLoading,
 *   isLearning,
 *   error,
 *   refresh,
 *   recalculate
 * } = useProcessVariance('strain-uuid')
 */

import { useState, useEffect, useCallback, useMemo } from 'react'
import { supabase } from '@/supabase-client'

// =============================================================================
// Constants
// =============================================================================

const FUNCTION_NAME = 'process-variance'
const MIN_SAMPLES_FOR_LEARNING = 10

// Valid process types for validation
const VALID_PROCESS_TYPES = ['lc', 'spawn', 'incubation', 'fruiting', 'substrate_prep']

// =============================================================================
// Edge Function Helper
// =============================================================================

/**
 * Call the process-variance Edge Function with REST-style routing
 *
 * @param {string} path - The endpoint path (e.g., 'predictions/uuid', 'statistics')
 * @param {Object} options - Fetch options
 * @param {string} [options.method='GET'] - HTTP method
 * @param {Object} [options.body] - Request body (for POST)
 * @returns {Promise<Object>} Response data
 * @throws {Error} On network or API error
 */
async function callProcessVarianceFunction(path, options = {}) {
  const { method = 'GET', body } = options

  const invokeOptions = { method }
  if (body) {
    invokeOptions.body = body
  }

  const { data, error } = await supabase.functions.invoke(
    `${FUNCTION_NAME}/${path}`,
    invokeOptions
  )

  if (error) {
    throw new Error(error.message || 'Edge function error')
  }

  // Check for API-level errors in response
  if (data?.error) {
    const err = new Error(data.message || data.error)
    err.retryable = data.retryable ?? false
    throw err
  }

  return data
}

// =============================================================================
// Main Hook: useProcessVariance
// =============================================================================

/**
 * Hook to fetch and manage process variance predictions for a strain
 *
 * @param {string} strainId - The strain UUID to fetch predictions for
 * @returns {Object} Hook state and actions
 * @property {Array} predictions - Array of PhasePrediction objects
 * @property {Array} statistics - Array of ProcessStatistics for the strain
 * @property {Array} seasonalPatterns - Array of SeasonalPattern objects
 * @property {Function} getPrediction - Get prediction for specific phase
 * @property {Function} getConfidence - Get confidence level for specific phase
 * @property {boolean} isLoading - True if any data is loading
 * @property {boolean} isLearning - True if any phase has < 10 samples
 * @property {string|null} error - Error message if any
 * @property {Function} refresh - Refetch all data
 * @property {Function} recalculate - Force recalculation of statistics
 */
export function useProcessVariance(strainId) {
  // Data state
  const [predictions, setPredictions] = useState([])
  const [statistics, setStatistics] = useState([])
  const [seasonalPatterns, setSeasonalPatterns] = useState([])

  // Loading states for each data type
  const [loadingPredictions, setLoadingPredictions] = useState(true)
  const [loadingStatistics, setLoadingStatistics] = useState(true)
  const [loadingSeasonalPatterns, setLoadingSeasonalPatterns] = useState(true)

  // Error state
  const [error, setError] = useState(null)

  // Combined loading state
  const isLoading = loadingPredictions || loadingStatistics || loadingSeasonalPatterns

  // Learning mode detection
  const isLearning = useMemo(() => {
    if (predictions.length === 0) return true
    return predictions.some(
      pred => (pred.prediction?.sample_count ?? 0) < MIN_SAMPLES_FOR_LEARNING
    )
  }, [predictions])

  // ==========================================================================
  // Data Fetching
  // ==========================================================================

  /**
   * Fetch predictions for the strain
   */
  const fetchPredictions = useCallback(async () => {
    if (!strainId) {
      setPredictions([])
      setLoadingPredictions(false)
      return
    }

    setLoadingPredictions(true)

    try {
      const response = await callProcessVarianceFunction(`predictions/${strainId}`)
      setPredictions(response.predictions || [])
    } catch (err) {
      console.error('[useProcessVariance] Predictions error:', err)
      setError(err.message)
      setPredictions([])
    } finally {
      setLoadingPredictions(false)
    }
  }, [strainId])

  /**
   * Fetch statistics for the strain
   */
  const fetchStatistics = useCallback(async () => {
    if (!strainId) {
      setStatistics([])
      setLoadingStatistics(false)
      return
    }

    setLoadingStatistics(true)

    try {
      const response = await callProcessVarianceFunction(`statistics/${strainId}`)
      setStatistics(response.statistics || [])
    } catch (err) {
      console.error('[useProcessVariance] Statistics error:', err)
      // Use functional update to check current error without adding it to deps
      setError(prevError => prevError || err.message)
      setStatistics([])
    } finally {
      setLoadingStatistics(false)
    }
  }, [strainId])

  /**
   * Fetch seasonal patterns for the strain
   */
  const fetchSeasonalPatterns = useCallback(async () => {
    if (!strainId) {
      setSeasonalPatterns([])
      setLoadingSeasonalPatterns(false)
      return
    }

    setLoadingSeasonalPatterns(true)

    try {
      const response = await callProcessVarianceFunction(`seasonal/${strainId}`)
      setSeasonalPatterns(response.patterns || [])
    } catch (err) {
      console.error('[useProcessVariance] Seasonal patterns error:', err)
      // Use functional update to check current error without adding it to deps
      setError(prevError => prevError || err.message)
      setSeasonalPatterns([])
    } finally {
      setLoadingSeasonalPatterns(false)
    }
  }, [strainId])

  // ==========================================================================
  // Getters
  // ==========================================================================

  /**
   * Get prediction for a specific phase
   *
   * @param {string} phase - The phase type ('lc', 'spawn', 'incubation', 'fruiting')
   * @returns {Object|null} PhasePrediction object or null if not found
   */
  const getPrediction = useCallback((phase) => {
    if (!phase || !VALID_PROCESS_TYPES.includes(phase)) {
      return null
    }
    return predictions.find(p => p.process_type === phase) || null
  }, [predictions])

  /**
   * Get confidence level for a specific phase
   *
   * @param {string} phase - The phase type
   * @returns {string|null} Confidence level ('high', 'medium', 'low', 'learning') or null
   */
  const getConfidence = useCallback((phase) => {
    const prediction = getPrediction(phase)
    return prediction?.prediction?.confidence_level ?? null
  }, [getPrediction])

  // ==========================================================================
  // Actions
  // ==========================================================================

  /**
   * Refresh all data (refetch predictions, statistics, and seasonal patterns)
   */
  const refresh = useCallback(async () => {
    setError(null)
    await Promise.all([
      fetchPredictions(),
      fetchStatistics(),
      fetchSeasonalPatterns()
    ])
  }, [fetchPredictions, fetchStatistics, fetchSeasonalPatterns])

  /**
   * Force recalculation of statistics for the strain
   *
   * @param {string} [processType] - Optional process type to recalculate (recalculates all if not specified)
   * @returns {Promise<Object>} Recalculation result
   */
  const recalculate = useCallback(async (processType = null) => {
    if (!strainId) {
      throw new Error('No strain ID provided')
    }

    const body = { strain_id: strainId }
    if (processType && VALID_PROCESS_TYPES.includes(processType)) {
      body.process_type = processType
    }

    try {
      const result = await callProcessVarianceFunction('recalculate', {
        method: 'POST',
        body
      })

      // Refresh data after recalculation
      await refresh()

      return result
    } catch (err) {
      console.error('[useProcessVariance] Recalculate error:', err)
      throw err
    }
  }, [strainId, refresh])

  // ==========================================================================
  // Effects
  // ==========================================================================

  // Fetch all data on mount and when strainId changes
  useEffect(() => {
    setError(null)
    fetchPredictions()
    fetchStatistics()
    fetchSeasonalPatterns()
  }, [fetchPredictions, fetchStatistics, fetchSeasonalPatterns])

  // ==========================================================================
  // Return
  // ==========================================================================

  return {
    // Data
    predictions,
    statistics,
    seasonalPatterns,

    // Per-phase getters
    getPrediction,
    getConfidence,

    // Status
    isLoading,
    isLearning,
    error,

    // Actions
    refresh,
    recalculate
  }
}

// =============================================================================
// Helper Hook: usePhasePrediction
// =============================================================================

/**
 * Simplified hook to fetch a single phase prediction
 *
 * @param {string} strainId - The strain UUID
 * @param {string} phase - The phase type ('lc', 'spawn', 'incubation', 'fruiting')
 * @returns {Object} Hook state
 * @property {Object|null} prediction - The PhasePrediction object
 * @property {boolean} isLoading - True while loading
 * @property {string|null} error - Error message if any
 * @property {Function} refresh - Refetch the prediction
 */
export function usePhasePrediction(strainId, phase) {
  const [prediction, setPrediction] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchPrediction = useCallback(async () => {
    if (!strainId || !phase) {
      setPrediction(null)
      setLoading(false)
      return
    }

    if (!VALID_PROCESS_TYPES.includes(phase)) {
      setError(`Invalid phase: ${phase}`)
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    try {
      const response = await callProcessVarianceFunction(`predictions/${strainId}/${phase}`)
      setPrediction(response.data || null)
    } catch (err) {
      console.error('[usePhasePrediction] Error:', err)
      setError(err.message)
      setPrediction(null)
    } finally {
      setLoading(false)
    }
  }, [strainId, phase])

  useEffect(() => {
    fetchPrediction()
  }, [fetchPrediction])

  return {
    prediction,
    isLoading: loading,
    error,
    refresh: fetchPrediction
  }
}

// =============================================================================
// Helper Hook: useProcessStatistics
// =============================================================================

/**
 * Hook to fetch process statistics only (without predictions or seasonal patterns)
 *
 * @param {string} [strainId] - Optional strain UUID to filter by
 * @returns {Object} Hook state
 * @property {Array} statistics - Array of ProcessStatistics
 * @property {Object|null} summary - Statistics summary (only when no strainId)
 * @property {boolean} isLoading - True while loading
 * @property {string|null} error - Error message if any
 * @property {Function} refresh - Refetch statistics
 */
export function useProcessStatistics(strainId = null) {
  const [statistics, setStatistics] = useState([])
  const [summary, setSummary] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchStatistics = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      const path = strainId ? `statistics/${strainId}` : 'statistics'
      const response = await callProcessVarianceFunction(path)

      if (strainId) {
        // Strain-specific response
        setStatistics(response.statistics || [])
        setSummary(null)
      } else {
        // All statistics response includes summary
        setStatistics(response.data || [])
        setSummary(response.summary || null)
      }
    } catch (err) {
      console.error('[useProcessStatistics] Error:', err)
      setError(err.message)
      setStatistics([])
      setSummary(null)
    } finally {
      setLoading(false)
    }
  }, [strainId])

  useEffect(() => {
    fetchStatistics()
  }, [fetchStatistics])

  return {
    statistics,
    summary,
    isLoading: loading,
    error,
    refresh: fetchStatistics
  }
}

// =============================================================================
// Default Export
// =============================================================================

export default useProcessVariance
